import { MouseEvent, RefObject, useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './Popover.module.css';

import { isOfType } from '../../utils/typeGuard';
import { createPortal } from 'react-dom';
import { POPOVER_MARGIN } from '../../constants';
import { isMobile } from '../../utils/util';

interface IPopover {
  name: string;
  content: string;
  onClose: (e: React.MouseEvent | MouseEvent | Event) => void;
  buttonRef: RefObject<HTMLElement>;
  placement?: string;
}

export default function Popover({
  name,
  content,
  onClose,
  buttonRef,
  placement,
}: IPopover) {
  const cx = classNames.bind(styles);

  const popoverRef = useRef<HTMLSpanElement>(null);
  const [popoverRect, setPopoverRect] = useState<DOMRect>(new DOMRect());
  const [buttonRect, setButtonRect] = useState<DOMRect>(new DOMRect());
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const clickPageHandler = (e: Event) => {
      if (
        isOfType<HTMLElement>(e.target, 'nodeName') &&
        !popoverRef?.current?.contains(e.target)
      ) {
        onClose(e);
      }
    };
    window.addEventListener('click', clickPageHandler);

    return () => {
      window.removeEventListener('click', clickPageHandler);
    };
  });

  useEffect(() => {
    const closePopover = (e: Event) => onClose(e);
    const detailsScrollWrapper = document.querySelector(
      '.simplebar-content-wrapper.details'
    );

    if (detailsScrollWrapper) {
      detailsScrollWrapper.addEventListener('scroll', closePopover, {
        passive: true,
      });
      return () => {
        detailsScrollWrapper.removeEventListener('scroll', closePopover);
      };
    }
  }, []);

  useEffect(() => {
    popoverRef.current &&
      setPopoverRect(popoverRef.current.getBoundingClientRect());
  }, [popoverRef.current]);

  useEffect(() => {
    buttonRef.current &&
      setButtonRect(buttonRef.current.getBoundingClientRect());
  }, [buttonRef.current]);

  useEffect(() => {
    const top = buttonRect.top - popoverRect.height - POPOVER_MARGIN;
    const isOver =
      buttonRect.left +
        popoverRect.width +
        parseInt(
          window.getComputedStyle(buttonRef.current as HTMLElement).marginLeft
        ) >
      window.outerWidth;
    let left = buttonRect.left - popoverRect.width / 2 + buttonRect.width / 2;

    left =
      left >= 0 && buttonRect.left + popoverRect.width / 2 < window.outerWidth
        ? left
        : isOver
        ? 10
        : buttonRect.left;

    setPosition({ top, left });
  }, [popoverRect, buttonRect]);

  if (!position.top && !position.left) return null;

  return createPortal(
    <span
      className={cx('popover', name)}
      ref={popoverRef}
      style={{
        top: position.top,
        left: position.left,
      }}
      dangerouslySetInnerHTML={{ __html: content }}
    ></span>,
    document.body
  );
}
